import { computed, inject, Injectable } from '@angular/core';
import { TableData } from 'src/app/shared/types/api-response.types';
import {
  ListingDataSource,
  ListingPageModel
} from '../../../shared/components/listing-page/services/listing-page-model/listing-page-model.types';
import { CustomersListingTableComponent } from './customers-listing-table.component';
import { OrderDirection } from '../../equipment/devices/components/devices.models';
import { CustomerResponse } from './api/customers-api.type';
import { CustomerSidePanelComponent } from './components/customer-side-panel/customer-side-panel.component';
import { CUSTOMER_SEARCH_MAX_LENGTH, CUSTOMERS_SEARCH_TYPES, tableConfig, tableSortFiled } from './customers.constants';
import { CustomerTable } from './customers.types';
import { CustomersService } from './services/customers.service';
import { TranslateRegistry } from 'src/app/services/translate/translate.registry';
import { ListingPageModelAbstract } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model-abstract';
import { ListingPageId } from '@portal/shared/components/listing-page/store/listing-page-store.types';
import { ListingPageModelRegistry } from '@portal/shared/components/listing-page/services/listing-page-model/listing-page-model-registry.service';
import { CustomersListingFiltersSourceService } from './customers-listing-filters-source.service';
import { ExportCsvConfig } from 'src/app/services/file/export-data.service';
import { Store } from '@ngrx/store';
import { isSingleCommodityClient } from 'src/app/pages/help-and-settings/user-settings/store/user-settings.reducer';

@Injectable()
export class CustomersListingModelService extends ListingPageModelAbstract implements ListingPageModel {
  private readonly customersService = inject(CustomersService);
  readonly listingPageModelRegistry = inject(ListingPageModelRegistry);
  readonly #isSingleCommodityClient = inject(Store).selectSignal(isSingleCommodityClient);

  readonly pageTitle = TranslateRegistry.get.CSR_CUSTOMERS.TITLE.CUSTOMERS;
  readonly searchTypeList = computed(() => CUSTOMERS_SEARCH_TYPES);
  readonly selectedSearchType = computed(() => CUSTOMERS_SEARCH_TYPES[0]);
  readonly searchTextMaxLength = CUSTOMER_SEARCH_MAX_LENGTH;
  readonly sortField = tableSortFiled;
  readonly sorting = computed(() => ({
    columnName: tableSortFiled.lastName!,
    orderDirection: OrderDirection.ASC
  }));

  readonly selectionFieldName = 'customerNumber';
  readonly tableColumns = computed(() =>
    tableConfig.map(col => (col.field === 'commodities' ? { ...col, isHidden: this.#isSingleCommodityClient() } : col))
  );
  readonly dataSource: ListingDataSource<CustomerTable> = {
    getAll: tableState => {
      return this.customersService.getTableData(tableState);
    },
    total: 0
  };
  readonly tableCmp = CustomersListingTableComponent;

  readonly sidePanelBodyCmp = CustomerSidePanelComponent;
  readonly sidePanelTitle = TranslateRegistry.get.CUSTOMERS_DETAILS.TITLE.CUSTOMER_DETAILS;

  readonly exportCsv = {
    fileName: 'Export-Customers',
    mapData: (data: TableData<CustomerResponse>): ExportCsvConfig[] => {
      return data.data.flatMap(customer =>
        customer.accounts.flatMap(account =>
          account.serviceDetails.flatMap((detail, detailIdx) =>
            detail.serviceLocationIds.map((locId, locIdx) => ({
              customerNumber: { value: customer.customerNumber, title: 'CustomerNumber' },
              customerStatus: { value: customer.customerStatus, title: 'CustomerStatus' },
              customerType: { value: customer.customerType, title: 'CustomerType' },
              lastName: { value: customer.lastName, title: 'FullName' },
              accountNumber: {
                value: detailIdx === 0 && locIdx === 0 ? `${account.accountNumber} / ${account.accountSubNumber}` : '',
                title: 'AccountNumber'
              },
              commodity: { value: locIdx === 0 ? detail.commodity : '', title: 'Commodity' },
              serviceLocationId: { value: locId, title: 'ServiceLocationNumber' },
              serviceLocationAddress: {
                value: detail.serviceLocationAddresses[locIdx] ?? '',
                title: 'ServiceLocationAddress'
              },
              phoneNumber: { value: customer.phoneNumber, title: 'PhoneNumber' },
              email: { value: customer.email, title: 'Email' }
            }))
          )
        )
      );
    }
  };
  readonly pageId = ListingPageId.CustomersList;
  readonly filterSource = inject(CustomersListingFiltersSourceService);

  constructor() {
    super();
    const listingPageModelRegistry = this.listingPageModelRegistry;

    listingPageModelRegistry.add(this.pageId, this);
  }
}
